import { json, type LoaderFunction, type MetaFunction } from '@remix-run/cloudflare';
import { useLocation, useNavigate } from '@remix-run/react';
import { useEffect, useState, useRef } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { BaseChat } from '~/components/chat/BaseChat.client';
import { Chat } from '~/components/chat/Chat.client';
import checkTokenAccess from '~/utils/checkTokenAccess';
import { useLoaderData } from '@remix-run/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { isDev } from '~/utils/constants';

export const meta: MetaFunction = () => {
  return [
    { title: 'Alpha' },
    { name: 'description', content: 'Talk with Alpha, an AI Frontend Developer from Dualite' },
  ];
};

// Loader function to expose environment variables
export const loader: LoaderFunction = async ({ context }) => {
  const cloudflareEnv = context.cloudflare.env;

  type AuthFrontendEnvs = {
    FIGMA_CLIENT_ID: string;
    FIGMA_REDIRECT_URI: string;
    DEV_BASE_URL: string;
    PROD_BASE_URL: string;
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_CLIENT_SECRET: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
    NEXT_PUBLIC_GITHUB_REPO: string;
    NEXT_PUBLIC_GITHUB_REDIRECT_URI: string;
    GITHUB_CLIENT_SECRET: string;
    GITHUB_CLIENT_ID: string;
  };

  const env: AuthFrontendEnvs = {
    FIGMA_CLIENT_ID: process.env.FIGMA_CLIENT_ID || cloudflareEnv.FIGMA_CLIENT_ID,
    FIGMA_REDIRECT_URI: process.env.FIGMA_REDIRECT_URI || cloudflareEnv.FIGMA_REDIRECT_URI,
    DEV_BASE_URL: process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV || cloudflareEnv.NEXT_PUBLIC_GET_ACCESS_URL_DEV,
    PROD_BASE_URL: process.env.NEXT_PUBLIC_GET_ACCESS_URL || cloudflareEnv.NEXT_PUBLIC_GET_ACCESS_URL,
    NEXT_PUBLIC_NETLIFY_CLIENT_ID:
      process.env.NEXT_PUBLIC_NETLIFY_CLIENT_ID || cloudflareEnv.NEXT_PUBLIC_NETLIFY_CLIENT_ID,
    NEXT_PUBLIC_NETLIFY_CLIENT_SECRET:
      process.env.NEXT_PUBLIC_NETLIFY_CLIENT_SECRET || cloudflareEnv.NEXT_PUBLIC_NETLIFY_CLIENT_SECRET,
    NEXT_PUBLIC_NETLIFY_APP_URL: process.env.NEXT_PUBLIC_NETLIFY_APP_URL || cloudflareEnv.NEXT_PUBLIC_NETLIFY_APP_URL,
    NEXT_PUBLIC_GITHUB_REPO: process.env.NEXT_PUBLIC_GITHUB_REPO || cloudflareEnv.NEXT_PUBLIC_GITHUB_REPO,
    NEXT_PUBLIC_GITHUB_REDIRECT_URI:
      process.env.NEXT_PUBLIC_GITHUB_REDIRECT_URI || cloudflareEnv.NEXT_PUBLIC_GITHUB_REDIRECT_URI,
    GITHUB_CLIENT_SECRET: process.env.GITHUB_CLIENT_SECRET || cloudflareEnv.GITHUB_CLIENT_SECRET,
    GITHUB_CLIENT_ID: process.env.GITHUB_CLIENT_ID || cloudflareEnv.GITHUB_CLIENT_ID,
  };

  return json(env);
};

export default function Index() {
  const { DEV_BASE_URL, PROD_BASE_URL } = useLoaderData<{
    DEV_BASE_URL: string;
    PROD_BASE_URL: string;
  }>();

  const navigate = useNavigate();
  const location = useLocation();
  const prevLocationRef = useRef(location);

  const [isLoading, setIsLoading] = useState(true);

  // Handle route changes
  useEffect(() => {
    const prevLocation = prevLocationRef.current;
    console.log('prevLocation', prevLocation.pathname);
    console.log('location', location.pathname);

    if (prevLocation.pathname === '/') {
      console.log('removing netlifySiteId and netlifySiteUrl');
      selectionStore.setKey('netlifySiteId', '');
      selectionStore.setKey('netlifySiteUrl', '');
    }

    prevLocationRef.current = location;
  }, []);

  // Check token access
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('dualitex-alpha-access-token');

      if (!token) {
        navigate('/activate-account');
      } else if (token && !isDev) {
        const { isActivated } = await checkTokenAccess(token, DEV_BASE_URL || PROD_BASE_URL);

        if (isActivated === 'INACTIVE' || isActivated === 'FAILED') {
          navigate('/activate-account');
        }

        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [navigate, DEV_BASE_URL, PROD_BASE_URL]);

  if (isLoading) {
    return null;
  }

  return (
    <div className="flex flex-col h-full w-full">
      {/* <Header /> */}
      <ClientOnly fallback={<BaseChat />}>{() => <Chat />}</ClientOnly>
    </div>
  );
}
